import { ReactElement } from 'react';
import cn from 'classnames';
import { useCopyToClipboard } from '@creditflowresearch/uilib/hooks/useCopyToClipboard';
import { differenceInHours, format, getTime, isSameDay } from 'date-fns';
import utcToZonedDate from '@framework/date-helpers/utcToZonedDate';
import type { ITimeZone } from '@dealroadshow/uikit-date-time';
import Tooltip from '@creditflowresearch/uilib/Tooltip';
import IconCopy from '@creditflowresearch/uilib/Icon/IconCopy';
import IconCheck from '@creditflowresearch/uilib/Icon/IconCheck';
import IconInvestorSet from '@creditflowresearch/uilib/Icon/IconInvestorSet';
import Label from '@creditflowresearch/uilib/Badge/Label';
import formatNumber from '@framework/data-helpers/formatters/formatNumber';
import { formatNumberWithDecimals } from '@/creditflowresearch/ui/components/deals/helpers';
import { getRatingValue, getBondsDate } from '@/creditflowresearch/ui/components/bonds/helpers';
import { EMPTY_CELL_STATE } from '@/creditflowresearch/ui/common/constants';
import { dataroomUrl } from '@dataroom/url';
import dataroomConfig from '@dataroom/config';

import {
  BWIC_DATE_FORMAT_EXPIRED,
  BWIC_DATE_FORMAT,
  INVENTORY_DATE_FORMAT, LABEL_COLOR_NEW,
} from '@/creditflowresearch/ui/components/bonds/constants';

import styles from '@/creditflowresearch/ui/components/bonds/common/BondTables/bondsTables.module.scss';
import linkStyles from '@creditflowresearch/uilib/styles/links.module.scss';
import { IDatetime } from '@framework/date-helpers/Datetime';

const IdentifierCell = (cellProps) => {
  let cellData = cellProps.row.cusip || cellProps.row.isin;
  let identifier = cellProps.row.cusip ? 'CUSIP' : 'ISIN';
  const [copy] = useCopyToClipboard();

  return (
    <>
      <input
        type="checkbox"
        id={ `identifier${ cellProps.row?.id || '' }${ cellProps.rowIndex }` }
        className={ cn('togglerCheckbox', styles.togglerCheckbox) }
      />

      <label
        htmlFor={ `identifier${ cellProps.row?.id || '' }${ cellProps.rowIndex }` }
        className={ styles.copy }
        data-test="identifierLabel"
      >
        { cellProps.row.deal?.investorSetAvailable && (
          <a
            target="_blank"
            className={ cn(linkStyles.link, styles.investorSetIconContainer) }
            href={ dataroomUrl(dataroomConfig.investorset.code)
              .getExploreSetsABSUrl() }
            data-test="investorSetEntryLink"
          >
            <Tooltip content="Transaction documentation is available on InvestorSet">
              <IconInvestorSet
                className={ styles.investorSetIcon }
              />
            </Tooltip>
          </a>
        ) }

        <span className={ linkStyles.link } onClick={ () => copy(cellData) }>
          <IconCopy
            className={ styles.iconCopy }
          />
          { cellData }
        </span>
      </label>

      <span className={ styles.copied }>
        <IconCheck
          className={ styles.iconCopied }
        />
        { identifier } Copied
      </span>
    </>
  );
};

export const getOriginalFaceCell = (cellProps) => {
  if (cellProps.cellData) {
    return formatNumber(cellProps.cellData);
  }

  return EMPTY_CELL_STATE;
};

export const getCurrentFaceCell = (cellProps) => {
  if (cellProps.cellData) {
    return formatNumber(cellProps.cellData);
  }

  return EMPTY_CELL_STATE;
};

export const getWalCell = (cellProps) => {
  if (cellProps.cellData) {
    return formatNumberWithDecimals(cellProps.cellData, 2);
  }

  return EMPTY_CELL_STATE;
};

export const getRatingCell = (cellProps) => getRatingValue(cellProps);

export const getOtherRatingCell = (cellProps) => {
  const value = getRatingValue(cellProps);
  const isValueTooLong = value.length > 6;

  return (
    <Tooltip
      content={ value }
      disabled={ !isValueTooLong }
    >
      { isValueTooLong ? `${ value.slice(0, 4) }...` : value }
    </Tooltip>
  );
};

export const getSpreadCell = (cellProps) => {
  const { row } = cellProps;
  let cell = '';

  if (row.benchmark) {
    cell = row.benchmark.abbreviation;
  }

  if (row.benchmark && row.spread !== null) {
    cell += '+';
  }

  if (row.spread !== null) {
    cell += row.spread;
  }

  return cell || EMPTY_CELL_STATE;
};

export const getSpeedCell = (cellProps) => {
  const { row } = cellProps;
  if (!row.speedType && !row.speed) {
    return EMPTY_CELL_STATE;
  }

  let cell = '';

  if (row.speed !== null) {
    // no decimals for zero value
    let isWholeNumber = row.speed % 1 !== 0;
    let decimals = (row.speed === 0) ? 0 : 2;
    cell = isWholeNumber ? `${ formatNumberWithDecimals(row.speed, decimals) } ` : `${ row.speed } `;
  }

  if (row.speedType && row.speedType.abbreviation) {
    cell += row.speedType.abbreviation;
  }

  return cell || EMPTY_CELL_STATE;
};

export const getCeCell = (cellProps) => {
  if (cellProps.cellData) {
    return formatNumberWithDecimals(cellProps.cellData, 2);
  }

  return EMPTY_CELL_STATE;
};

export const getPriceTalkCell = (cellProps) => {
  if (Number(cellProps.cellData) === 0) {
    return EMPTY_CELL_STATE;
  }

  return cellProps.cellData;
};

export const getDealerCell = (cellProps) => {
  const { name, abbreviation } = cellProps.cellData;
  const { lastImportAt } = cellProps.row;

  return (
    <Tooltip
      placement="bottom"
      content={ getTooltip(name, lastImportAt) }
    >
      { abbreviation }
    </Tooltip>
  );
};

const getTooltip = (name: string, lastImportAt: IDatetime): ReactElement => (
  <div className={ styles.tooltipContainer }>
    <div><span className={ styles.tooltipLabel }>Dealer Name </span> { name } </div>
    <div><span className={ styles.tooltipLabel }>Last Update </span> { getBondsDate(lastImportAt) } </div>
  </div>
);

/**
 * CUSIP / ISIN cell
 * Due to issues with setting row state as hovered after onmouseenter and setting unhovered state with omouseleave
 * (when you moved your cursor super fast - onmouselave was registered on another row or wasn't registered
 * at all - that's why this trick with checkbox+label was used
 */
export const getIdentifierCell = (cellProps) => <IdentifierCell { ...cellProps } />;

export const getListIdCell = (cellProps, onListIdClick) => {
  if (cellProps.cellData) {
    return (
      <Tooltip content="View BWIC List">
        <a
          className={ linkStyles.link }
          onClick={ () => onListIdClick(cellProps.cellData) }
        >
          { cellProps.cellData }
        </a>
      </Tooltip>
    );
  }

  return EMPTY_CELL_STATE;
};

export const getDueByCell = ({ row }, timeZone: ITimeZone) => {
  if (!row.dueBy) {
    return EMPTY_CELL_STATE;
  }
  const { dateTime } = row.dueBy;

  const now = getTime(utcToZonedDate(timeZone.timeZone));
  const dueBy = getTime(utcToZonedDate(timeZone.timeZone, new Date(dateTime)));

  // if bond has expired more than 24 hours ago
  if (differenceInHours(now, dueBy) >= 24) {
    return format(dueBy, BWIC_DATE_FORMAT_EXPIRED);
  }

  return format(dueBy, BWIC_DATE_FORMAT);
};

export const getPriceCell = (cellProps) => {
  if (Number(cellProps.cellData) === 0) {
    return EMPTY_CELL_STATE;
  }

  return cellProps.cellData;
};

export const getAddedAtCell = ({ row }) => {
  const date = row.addedAt;
  const { timezone, dateTime } = date;
  const time = utcToZonedDate(timezone.timeZone, new Date(dateTime));

  const isToday = isSameDay(time, utcToZonedDate(timezone.timeZone));

  if (isToday) {
    return 'Today';
  }

  return format(time, INVENTORY_DATE_FORMAT);
};

export const getIsNewCell = (cellProps) => {
  if (cellProps.cellData) {
    return (
      <Label backgroundColor={ LABEL_COLOR_NEW }>
        NEW
      </Label>
    );
  }

  return undefined;
};
